import { useMemo } from 'react';
import { useQuery } from '@apollo/react-hooks';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/es';

import LIVE_CONSUMPTION from '../../../../graphql/queries/live_consumption';
import { chartMaxValue } from '../../../shared/sharedFunctions/sharedFunctions';

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.locale('es');

const reducedDates = (data) => {
  return data.reduce((acc, item) => {
    const value = Number((item.y).toFixed(2));
    const date = item.x;
    const dayjsDate = dayjs(date, "DD/MM/YYYY");

        acc[date] = {
         value,
         dayjsDate,
       }

      return acc;
  }, {});
}

export const useLiveConsumption = (clientId, startDate, endDate) => {
  let minValue = Infinity;
  let maxValue = -Infinity;
  const { loading, error, data } = useQuery(LIVE_CONSUMPTION, {
    variables: {clientId, startDate, endDate},
  });

  const graphData = useMemo(() => {
    const cfeConsumption = data?.liveConsumption?.cfeConsumption || [];
    const solarConsumption = data?.liveConsumption?.solarConsumption || [];
    const injectedGeneration = data?.liveConsumption?.injectedGeneration || [];

    if (
      cfeConsumption.length || solarConsumption.length ||
      injectedGeneration.length
    ) {

      const reducedCfeConsumptionDates = reducedDates(cfeConsumption);
      const reducedSolarConsumptionDates = reducedDates(solarConsumption);
      const reducedInjectedGenerationDates = reducedDates(injectedGeneration);

      const dates = [
        ...Object.keys(reducedCfeConsumptionDates),
        ...Object.keys(reducedSolarConsumptionDates),
        ...Object.keys(reducedInjectedGenerationDates)
      ].sort((a, b) => {
        const dateA = dayjs(a, 'DD/MM/YYYY', 'es');
        const dateB = dayjs(b, 'DD/MM/YYYY', 'es');

        return dateA.isBefore(dateB) ? -1 : 1;
      })

      const uniqueDates = Array.from(new Set(dates));
      
      const days = uniqueDates.map(date => {
         let newFormatDate = dayjs(date, "DD/MM/YYYY").format('DD/MM/YYYY');
         newFormatDate = newFormatDate.charAt(0).toUpperCase() + newFormatDate.slice(1);
         newFormatDate = newFormatDate.replace('.-', ' ');

         return {
           date: newFormatDate,
           cfeConsumption: reducedCfeConsumptionDates[date]?.value || 0,
           solarConsumption: reducedSolarConsumptionDates[date]?.value || 0,
           consumedGeneration: reducedSolarConsumptionDates[date]?.value || 0,
           injectedGeneration: reducedInjectedGenerationDates[date]?.value || 0
         }
      });
      return days;
    }

    return [];
  }, [data]);
  if (graphData.length) {
    graphData.forEach(data => {
       const consumption = data.solarConsumption + data.cfeConsumption;
       const generation = data.consumedGeneration + data.injectedGeneration;

       minValue = Math.min(minValue, consumption, generation);
       maxValue = chartMaxValue(Math.max(maxValue, consumption, generation));
    })
  }

  return {
    loading,
    error,
    graphData,
    minValue: Math.floor(minValue),
    maxValue: Math.ceil(maxValue),
  };
};
